import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  supabase,
  dbError,
  getSignedUrlRefreshDelay,
  addSignedUrlsToPhotos,
  LIST_PHOTO_VARIANTS,
  isTransientNetworkError,
} from '@/lib';
import { MemberProfilePhotoData } from '@/types';
import { mapSupabaseError } from '@/lib';
import { MEMBER_PHOTO_QUERY_KEYS } from '@/constants';

const PROFILE_PHOTO_BUCKET = 'member-uploaded-photos';

type RawMemberProfilePhoto = MemberProfilePhotoData;

export function useMemberUploadedPhotos(memberId: string) {
  const query = useQuery({
    queryKey: [MEMBER_PHOTO_QUERY_KEYS.UPLOADED_BY_MEMBER_PREFIX, memberId],
    queryFn: async () => {
      try {
        if (!memberId) {
          throw new Error('Member ID is required');
        }

        // Fetch latest profile photo
        const { data, error } = await supabase
          .from('member_uploaded_photos')
          .select('id, original_photo_url, thumbnail_photo_url, large_photo_url, uploaded_at')
          .eq('member_id', memberId)
          .order('uploaded_at', { ascending: false })
          .limit(1);

        if (error) {
          throw mapSupabaseError(error, 'fetching profile photos', { memberId });
        }

        const photosWithSignedUrls = await addSignedUrlsToPhotos(
          (data || []) as RawMemberProfilePhoto[],
          PROFILE_PHOTO_BUCKET
        );

        return photosWithSignedUrls;
      } catch (err) {
        dbError('Unexpected error in useMemberUploadedPhotos', err, { memberId });
        throw err;
      }
    },
    enabled: !!memberId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
    // Tab-click on the member-profile screen remounts this hook;
    // refetch each time so admins see fresh data on every tab visit.
    refetchOnMount: 'always',
  });

  const { data, refetch } = query;

  useEffect(() => {
    if (!data?.length) {
      return;
    }

    const earliestExpiry = data.reduce<number | null>((min, photo) => {
      const expiries = [
        photo.original_photo_url_expires_at,
        photo.large_photo_url_expires_at,
        photo.thumbnail_photo_url_expires_at,
      ].filter((value): value is number => typeof value === 'number' && !Number.isNaN(value));

      if (!expiries.length) {
        return min;
      }

      const photoMin = Math.min(...expiries);
      if (min === null || photoMin < min) {
        return photoMin;
      }
      return min;
    }, null);

    const delay = getSignedUrlRefreshDelay(earliestExpiry);
    if (delay === null) {
      return;
    }

    const timer = setTimeout(() => {
      refetch();
    }, delay);

    return () => clearTimeout(timer);
  }, [data, refetch]);

  return query;
}

export function useAllMemberProfilePhotos(memberId: string) {
  const query = useQuery({
    queryKey: MEMBER_PHOTO_QUERY_KEYS.ALL_PROFILE_BY_MEMBER(memberId),
    queryFn: async () => {
      try {
        if (!memberId) {
          throw new Error('Member ID is required');
        }

        // Fetch ALL profile photos (no limit)
        const { data, error } = await supabase
          .from('member_uploaded_photos')
          .select('id, original_photo_url, thumbnail_photo_url, large_photo_url, uploaded_at')
          .eq('member_id', memberId)
          .order('uploaded_at', { ascending: false });

        if (error) {
          throw mapSupabaseError(error, 'fetching all profile photos', { memberId });
        }

        const photosWithSignedUrls = await addSignedUrlsToPhotos(
          (data || []) as RawMemberProfilePhoto[],
          PROFILE_PHOTO_BUCKET
        );

        return photosWithSignedUrls;
      } catch (err) {
        dbError('Unexpected error in useAllMemberProfilePhotos', err, { memberId });
        throw err;
      }
    },
    enabled: !!memberId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
  });

  const { data, refetch } = query;

  useEffect(() => {
    if (!data?.length) {
      return;
    }

    const earliestExpiry = data.reduce<number | null>((min, photo) => {
      const expiries = [
        photo.original_photo_url_expires_at,
        photo.large_photo_url_expires_at,
        photo.thumbnail_photo_url_expires_at,
      ].filter((value): value is number => typeof value === 'number' && !Number.isNaN(value));

      if (!expiries.length) {
        return min;
      }

      const photoMin = Math.min(...expiries);
      if (min === null || photoMin < min) {
        return photoMin;
      }
      return min;
    }, null);

    const delay = getSignedUrlRefreshDelay(earliestExpiry);
    if (delay === null) {
      return;
    }

    const timer = setTimeout(() => {
      refetch();
    }, delay);

    return () => clearTimeout(timer);
  }, [data, refetch]);

  return query;
}

export function useBulkMemberProfilePhotos(memberIds: string[]) {
  const query = useQuery({
    queryKey: MEMBER_PHOTO_QUERY_KEYS.BULK_PROFILE(memberIds),
    queryFn: async () => {
      if (!memberIds.length) {
        return {};
      }

      // Filter out invalid UUIDs to prevent database errors
      const validMemberIds = memberIds.filter(id => {
        const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
        return id && typeof id === 'string' && uuidRegex.test(id);
      });

      if (!validMemberIds.length) {
        return {};
      }

      // RPC parameters are sent in a POST body. The previous `.in()` GET put
      // every UUID in the URL and crossed the gateway limit at 648 members
      // (HOK-OS-50). The function also returns only the latest active photo,
      // avoiding historical-photo fan-out and PostgREST's response-row cap.
      const { data, error } = await supabase.rpc('get_latest_uploaded_photo_per_member', {
        p_member_ids: validMemberIds,
      });

      if (error) {
        // mapSupabaseError reports the original database failure once. Do not
        // catch and report the mapped Error again below.
        throw mapSupabaseError(error, 'fetching profile photos', {
          memberCount: memberIds.length,
          validMemberCount: validMemberIds.length,
        });
      }

      const rows = (data ?? []) as Array<RawMemberProfilePhoto & { member_id: string }>;
      // Sign only the sizes a list/grid renders — see LIST_PHOTO_VARIANTS.
      const photosWithSignedUrls = await addSignedUrlsToPhotos(
        rows,
        PROFILE_PHOTO_BUCKET,
        LIST_PHOTO_VARIANTS,
      );

      const photosByMember: Record<string, MemberProfilePhotoData & {
        original_photo_path: string | null;
        thumbnail_photo_path: string | null;
        large_photo_path: string | null;
        original_photo_url_expires_at: number | null;
        thumbnail_photo_url_expires_at: number | null;
        large_photo_url_expires_at: number | null;
      }> = {};

      rows.forEach((photo, index) => {
        const signedPhoto = photosWithSignedUrls[index];
        photosByMember[photo.member_id] = {
          id: photo.id,
          original_photo_url: signedPhoto?.original_photo_url ?? null,
          thumbnail_photo_url: signedPhoto?.thumbnail_photo_url ?? null,
          large_photo_url: signedPhoto?.large_photo_url ?? null,
          uploaded_at: photo.uploaded_at,
          original_photo_path: signedPhoto?.original_photo_path ?? null,
          thumbnail_photo_path: signedPhoto?.thumbnail_photo_path ?? null,
          large_photo_path: signedPhoto?.large_photo_path ?? null,
          original_photo_url_expires_at: signedPhoto?.original_photo_url_expires_at ?? null,
          thumbnail_photo_url_expires_at: signedPhoto?.thumbnail_photo_url_expires_at ?? null,
          large_photo_url_expires_at: signedPhoto?.large_photo_url_expires_at ?? null,
        };
      });

      return photosByMember;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    // A permanent 4xx must not be retried and multiplied in Sentry. Preserve
    // bounded retries for Safari/Chrome network blips only.
    retry: (failureCount, error) =>
      failureCount < 2 && isTransientNetworkError(error),
  });

  const { data, refetch } = query;

  useEffect(() => {
    const photoValues = data ? Object.values(data) : [];
    if (!photoValues.length) {
      return;
    }

    const earliestExpiry = photoValues.reduce<number | null>((min, photo) => {
      const expiries = [
        photo.original_photo_url_expires_at,
        photo.large_photo_url_expires_at,
        photo.thumbnail_photo_url_expires_at,
      ].filter((value): value is number => typeof value === 'number' && !Number.isNaN(value));

      if (!expiries.length) {
        return min;
      }

      const photoMin = Math.min(...expiries);
      if (min === null || photoMin < min) {
        return photoMin;
      }
      return min;
    }, null);

    const delay = getSignedUrlRefreshDelay(earliestExpiry);
    if (delay === null) {
      return;
    }

    const timer = setTimeout(() => {
      refetch();
    }, delay);

    return () => clearTimeout(timer);
  }, [data, refetch]);

  return query;
}
